import {
  Box,
  Button,
  Card,
  Dialog,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import React, { FC, useEffect, useState } from 'react';
import { useDataProvider, useRedirect, useRefresh } from 'react-admin';
import { getDate } from '../../helpers/dateHelper';
import { getFieldByTitle } from '../../helpers/sortByTitle';
import { Referral } from '../../types/referrals';
import PaginationTable from '../common/paginationTable';
import HeadTable from '../common/sortTable/sortTable';
import SettingsIcon from '@material-ui/icons/Settings';
import AddIcon from '@material-ui/icons/Add';
import './index.css';
import AddReferral from './AddReferral';
import CreateReferral from './CreateReferral';
import dataProvider from '../../providers/dataProvider';
import { parsePhone } from '../../helpers/parsePhone';

interface Props {
  id: string;
  isView: boolean;
}

interface NewValueProps {
  referral: Referral | undefined;
  closeModal: () => void;
}

interface ModalState {
  add: boolean;
  create: boolean;
  limit: boolean;
  selectedReferral: undefined | Referral;
}

const ReferralTab: FC<Props> = ({ id, isView }) => {
  const dataProvider = useDataProvider();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const [state, setState] = useState({
    referrals: Array<Referral>(),
    total: 0,
    page: 1,
    perPage: 10,
    field: '_id',
    order: 'asc',
  });

  const [modal, setModal] = useState<ModalState>({
    add: false,
    create: false,
    limit: false,
    selectedReferral: undefined,
  });

  useEffect(() => {
    getReferrals(1, 10, '_id', 'asc');
  }, []);

  const getReferrals = async (
    page: number,
    perPage: number,
    field: string,
    order: string,
  ) => {
    const result = await dataProvider.getUserReferrals('', {
      id: id,
      pagination: { page: page, perPage: perPage },
      sort: { field: field, order: order },
      filter: {},
    });
    setState({
      ...state,
      referrals: result.data,
      total: result.total,
      page: page,
      perPage: perPage,
      field: field,
      order: order,
    });
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ): void => {
    getReferrals(newPage + 1, state.perPage, state.field, state.order);
  };

  const handleLastPage = () => {
    const page = Math.ceil(state.total / state.perPage);
    getReferrals(page, state.perPage, state.field, state.order);
  };

  const handleFirstPage = () => {
    getReferrals(1, state.perPage, state.field, state.order);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const perPage = parseInt(event.target.value, 10);
    getReferrals(1, perPage, state.field, state.order);
  };

  const handleSort = (direction: string, title: string) => {
    let field = getFieldByTitle(title);
    field = field === 'createdDate' ? 'createdAt' : field;
    getReferrals(state.page, state.perPage, field, direction);
  };

  const openModal = (property: string) => {
    setModal({ ...modal, [property]: true });
  };

  const closeModal = () => {
    setModal({ ...modal, add: false, create: false, limit: false });
  };

  const transitionByModal = (addView: boolean, createView: boolean) => {
    setModal({ ...modal, add: addView, create: createView });
  };

  const toUser = (id: string) => {
    redirect(`/users/${id}`);
    refresh();
  };

  return (
    <>
      <Box marginTop='0.5em'>
        <Card>
          <Box padding='2em' display='flex' justifyContent='space-between'>
            <Typography className='base-color'>
              Total referrals attracted: {state.total}
            </Typography>
            {!isView && (
              <div className='add-referral' onClick={() => openModal('add')}>
                <AddIcon />
                Add new
              </div>
            )}
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell id='active-table-head'>
                    <HeadTable title='Date' sort={handleSort} />
                  </TableCell>
                  <TableCell id='active-table-head'>
                    <HeadTable title='First name' sort={handleSort} />
                  </TableCell>
                  <TableCell id='active-table-head'>
                    <HeadTable title='Last name' sort={handleSort} />
                  </TableCell>
                  <TableCell id='active-table-head'>
                    <HeadTable title='Email address' sort={handleSort} />
                  </TableCell>
                  <TableCell id='active-table-head'>
                    <HeadTable title='Phone' sort={handleSort} />
                  </TableCell>
                  <TableCell id='active-table-head'>
                    <HeadTable
                      title='Spent/Needed to spend'
                      sort={handleSort}
                    />
                  </TableCell>
                  {!isView && (
                    <TableCell>
                      <Box display='flex' justifyContent='center'>
                        Actions
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              {state.referrals.map((item) => {
                return (
                  <TableBody>
                    <TableCell>{getDate(new Date(item.createdAt))}</TableCell>
                    <TableCell>
                      <div
                        className='move-search-user'
                        onClick={() => toUser(item._id)}
                      >
                        {' '}
                        {item.name}{' '}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div
                        className='move-search-user'
                        onClick={() => toUser(item._id)}
                      >
                        {item.surname}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div
                        className='move-search-user'
                        onClick={() => toUser(item._id)}
                      >
                        {item.email}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div
                        className='move-search-user'
                        onClick={() => toUser(item._id)}
                      >
                        {parsePhone(item.phone)}
                      </div>
                    </TableCell>
                    <TableCell>
                      {item.spentMoney / 100}/{item.neededSpend / 100}
                    </TableCell>
                    {!isView && (
                      <TableCell>
                        {item.spentMoney === 0 ? (
                          <Box
                            display='flex'
                            justifyContent='center'
                            className='setting-icon'
                            onClick={() => {
                              setModal({
                                ...modal,
                                limit: true,
                                selectedReferral: item,
                              });
                            }}
                          >
                            <SettingsIcon />
                          </Box>
                        ) : (
                          <Box
                            display='flex'
                            justifyContent='center'
                            className='setting-disabled-icon'
                          >
                            <SettingsIcon />
                          </Box>
                        )}
                      </TableCell>
                    )}
                  </TableBody>
                );
              })}
            </Table>
          </TableContainer>
          <PaginationTable
            page={state.page}
            perPage={state.perPage}
            collectionLength={state.referrals.length}
            total={state.total}
            handleChangePage={handleChangePage}
            handleLastPage={handleLastPage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleFirstPage={handleFirstPage}
            title='Orders'
          />
          <Dialog
            fullWidth
            open={modal.add}
            onClose={closeModal}
            className='add-referral-modal'
          >
            <DialogContent>
              <AddReferral
                closeModal={closeModal}
                openCreate={transitionByModal}
                id={id}
              />
            </DialogContent>
          </Dialog>
          <Dialog
            fullWidth
            open={modal.create}
            onClose={closeModal}
            className='add-referral-modal'
          >
            <DialogContent>
              <CreateReferral
                openAdd={transitionByModal}
                closeModal={closeModal}
                id={id}
              />
            </DialogContent>
          </Dialog>
          <Dialog
            fullWidth
            open={modal.limit}
            onClose={closeModal}
            className='add-referral-modal'
          >
            <DialogContent>
              <NewValue
                referral={modal.selectedReferral}
                closeModal={closeModal}
              />
            </DialogContent>
          </Dialog>
        </Card>
      </Box>
    </>
  );
};

export default ReferralTab;

const NewValue: FC<NewValueProps> = ({ referral, closeModal }) => {
  const refresh = useRefresh();
  const [state, setState] = useState(1);

  useEffect(() => {
    if (referral) {
      setState(referral.neededSpend / 100);
    }
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const count = parseInt(event.currentTarget.value);
    if (count < 1) {
      return;
    }
    setState(count);
  };

  const saveChanges = async (): Promise<void> => {
    if (referral) {
      referral.neededSpend = state * 100;
      referral.phone = referral.phone ? parsePhone(referral.phone) : '-';
      const result = await dataProvider.update('users/cms', {
        id: referral._id,
        data: referral,
      });
      if (result.data) {
        refresh();
      }
    }
  };

  return (
    <>
      <Box display='flex' justifyContent='center'>
        <Typography className='base-color' variant='h6'>
          Set new default value
        </Typography>
      </Box>
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        padding='2em'
      >
        <Typography className='base-color'>new default amount</Typography>
        <TextField
          type='number'
          label=''
          className='add-tickets-field'
          value={state}
          inputProps={{ step: 1 }}
          onChange={handleChange}
          defaultValue={1}
          required
        />
      </Box>

      <Box padding='4em'>
        <Box
          display='flex'
          justifyContent='space-evenly'
          alignItems='center'
          paddingTop='1em'
        >
          <Button
            className='user-cancel-button user-modal-buttons'
            disabled={!state}
            onClick={saveChanges}
          >
            Save
          </Button>
          <Button
            className='user-cancel-button user-modal-buttons'
            onClick={closeModal}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </>
  );
};
